import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faDownload, 
  faChartLine, 
  faQuestionCircle, 
  faUsers,
  faLightbulb,
  faFilter,
  faBookmark,
  faClock,
  faArrowUp,
  faArrowDown,
  faChevronDown,
  faChevronRight,
  faPlay,
  faEye,
  faMagic,
  faCheckCircle,
  faBrain,
  faDna,
  faHeartbeat
} from '@fortawesome/free-solid-svg-icons';
import PieChart from '../components/PieChart';
import './EngagementDataExplorer.css';

// Simulated data generation
const generateMockData = () => {
  const concepts = [
    { name: 'Alpha-synuclein aggregation', category: 'Mechanisms', baseEngagement: 450 },
    { name: 'Gut-brain axis', category: 'Mechanisms', baseEngagement: 380 },
    { name: 'Dopamine replacement therapy', category: 'Treatments', baseEngagement: 520 },
    { name: 'Constipation & autonomic dysfunction', category: 'Symptoms', baseEngagement: 340 },
    { name: 'Disease progression stages', category: 'Progression', baseEngagement: 410 },
    { name: 'Neuroinflammation', category: 'Mechanisms', baseEngagement: 390 },
    { name: 'Clinical trial eligibility', category: 'Trials', baseEngagement: 470 },
    { name: 'REM sleep behavior disorder', category: 'Symptoms', baseEngagement: 310 },
    { name: 'Deep brain stimulation', category: 'Treatments', baseEngagement: 440 },
    { name: 'Lewy body formation', category: 'Mechanisms', baseEngagement: 360 },
    { name: 'Gait & balance challenges', category: 'Symptoms', baseEngagement: 395 },
    { name: 'Medication timing & efficacy', category: 'Treatments', baseEngagement: 425 }
  ];

  const questionClusters = [
    { 
      name: 'Astrophysics & Space',
      questions: 742,
      growth: 28,
      examples: [
        'How do we study exoplanet atmospheres?',
        'What can gravitational waves tell us about the universe?',
        'How does machine learning help astronomy?'
      ]
    },
    { 
      name: 'Quantum Computing',
      questions: 589,
      growth: 45,
      examples: [
        'How do quantum gates actually work?',
        'What is quantum error correction?',
        'When will quantum computers be practical?'
      ]
    },
    { 
      name: 'AI & Machine Learning',
      questions: 856,
      growth: 52,
      examples: [
        'How do neural networks learn?',
        'What are generative AI models?',
        'Can AI help solve scientific problems?'
      ]
    },
    { 
      name: 'Neuroscience',
      questions: 634,
      growth: 18,
      examples: [
        'How do neural networks in the brain process information?',
        'What causes neurodegenerative diseases?',
        'How does memory formation work?'
      ],
      publications: [
        {
          title: 'Early excitatory-inhibitory cortical modifications following skill learning are associated with motor memory consolidation',
          authors: 'Weizmann Institute of Science',
          journal: 'Nature Communications, 2024',
          journalLogo: '/snorkle_vis_thumbnails/nature-logo.png',
          url: 'https://weizmann.elsevierpure.com/en/publications/early-excitatory-inhibitory-cortical-modifications-following-skil/'
        },
        {
          title: 'Human hippocampal and entorhinal neurons encode the temporal structure of experience',
          authors: 'Weizmann Institute researchers',
          journal: 'Nature, 2024',
          journalLogo: '/snorkle_vis_thumbnails/nature-logo.png',
          url: 'https://www.nature.com/articles/s41586-024-07973-1'
        },
        {
          title: 'Perivascular cells induce microglial phagocytic states and synaptic engulfment via SPP1 in mouse models of Alzheimer\'s disease',
          authors: 'Weizmann Institute of Science',
          journal: 'Nature Neuroscience, 2023',
          journalLogo: '/snorkle_vis_thumbnails/natureneurologo.jpg',
          url: 'https://weizmann.elsevierpure.com/en/publications/perivascular-cells-induce-microglial-phagocytic-states-and-synapt'
        },
        {
          title: 'Determinants of functional synaptic connectivity among amygdala-projecting prefrontal cortical neurons in male mice',
          authors: 'Weizmann Institute of Science',
          journal: 'Cell Reports, 2023',
          journalLogo: '/snorkle_vis_thumbnails/nature-logo.png',
          url: 'https://weizmann.elsevierpure.com/en/publications/determinants-of-functional-synaptic-connectivity-among-amygdala-p-2/'
        }
      ]
    },
    { 
      name: 'Climate & Environment',
      questions: 551,
      growth: 22,
      examples: [
        'What drives regional climate patterns?',
        'How do we model future climate scenarios?',
        'What are climate feedback loops?'
      ]
    }
  ];

  const audienceSegments = [
    { 
      role: 'Patients', 
      count: 3240, 
      avgTime: '8:45',
      topInterests: ['Symptoms & daily life', 'Treatments', 'Disease progression'],
      readingStyle: 'Visual-first',
      engagement: 'Deep explainer'
    },
    { 
      role: 'Caregivers', 
      count: 2180, 
      avgTime: '12:20',
      topInterests: ['Disease progression', 'Lifestyle management', 'Clinical trials'],
      readingStyle: 'Comprehensive',
      engagement: 'Deep explainer'
    },
    { 
      role: 'Researchers', 
      count: 890, 
      avgTime: '15:30',
      topInterests: ['Mechanisms', 'Clinical trials', 'Latest research'],
      readingStyle: 'Technical',
      engagement: 'Deep explainer'
    },
    { 
      role: 'Healthcare providers', 
      count: 650, 
      avgTime: '6:15',
      topInterests: ['Treatments', 'Disease progression', 'Patient education'],
      readingStyle: 'Quick answers',
      engagement: 'Quick answers'
    },
    { 
      role: 'General public', 
      count: 1420, 
      avgTime: '4:30',
      topInterests: ['Basic understanding', 'Symptoms', 'Prevention'],
      readingStyle: 'Visual-first',
      engagement: 'Quick answers'
    }
  ];

  const weeklyData = Array.from({ length: 12 }, (_, i) => {
    const weekAgo = 12 - i;
    return {
      week: `Week ${i + 1}`,
      visitors: 1200 + Math.floor(Math.random() * 400),
      repeatVisitors: 450 + Math.floor(Math.random() * 150),
      avgTimeMinutes: 7 + Math.random() * 3,
      interactionRate: 65 + Math.random() * 15
    };
  });

  const conceptPaths = [
    { 
      entry: 'Constipation symptoms', 
      followUp: ['Gut-brain axis', 'Neuroinflammation', 'Autonomic dysfunction'],
      frequency: 234
    },
    { 
      entry: 'Dopamine replacement', 
      followUp: ['Medication timing', 'Side effects', 'Alternative treatments'],
      frequency: 189
    },
    { 
      entry: 'Disease progression', 
      followUp: ['Clinical trials', 'Neuroprotective strategies', 'Biomarkers'],
      frequency: 156
    },
    { 
      entry: 'Clinical trial eligibility', 
      followUp: ['Study phases', 'Placebo effects', 'Trial locations'],
      frequency: 142
    }
  ];

  const suggestedContent = [
    {
      id: 1,
      title: 'Deep Learning Unveils Jupiter\'s Hidden Layers',
      type: 'Interactive 3D Model',
      description: 'Explore how AI is revolutionizing our understanding of gas giant planets by analyzing gravity data from NASA\'s Juno spacecraft',
      publication: 'Astronomy & Astrophysics, 2024 - Helled et al. (Weizmann) - NeuralCMS: A deep learning approach to study Jupiter\'s interior',
      publicationUrl: 'https://www.aanda.org/articles/aa/full_html/2024/06/aa50223-24/aa50223-24.html',
      dataNeeded: 'Juno gravity field coefficients, trained neural network model files, interior structure diagrams',
      figureUrl: '/api/placeholder/figure1',
      color: '#8b5cf6'
    },
    {
      id: 2,
      title: 'Your Body\'s Molecular Shredder Fights Infection',
      type: 'Molecular 3D Visualization',
      description: 'Discover how proteasomes - the cell\'s recycling machines - create powerful antimicrobial weapons from broken-down proteins',
      publication: 'Nature, 2025 - Merbl Lab (Weizmann) - Cell-autonomous innate immunity by proteasome-derived defence peptides',
      publicationUrl: 'https://www.nature.com/articles/s41586-025-08615-w',
      dataNeeded: 'Proteasome structure PDB files, peptide sequences, mass spectrometry data',
      figureUrl: '/api/placeholder/figure2',
      color: '#ec4899'
    },
    {
      id: 3,
      title: 'Building a Quantum Computer with Trapped Atoms',
      type: 'Explainer Video',
      description: 'See how scientists are harnessing individual atoms suspended in space to create the computers of tomorrow',
      publication: 'Nature, 2024 - Manovitz et al. (Weizmann) - Logical quantum processor based on reconfigurable atom arrays',
      publicationUrl: 'https://www.nature.com/articles/s41586-023-06927-3',
      dataNeeded: 'Quantum circuit diagrams, atom trap schematics, error rate data visualization',
      figureUrl: '/api/placeholder/figure3',
      color: '#f59e0b'
    },
    {
      id: 4,
      title: 'AI Recreates Particle Collisions in Milliseconds',
      type: 'Scrollytelling Story',
      description: 'Watch how artificial intelligence is accelerating physics research by simulating detector responses 1000x faster than traditional methods',
      publication: 'Reports on Progress in Physics, 2024 - Krause, Faucci Giannelli, Gross et al. (incl. Weizmann) - Fast Calorimeter Simulation Challenge',
      publicationUrl: 'https://arxiv.org/abs/2410.21611',
      dataNeeded: 'Detector geometry, particle shower images, GAN/diffusion model outputs',
      figureUrl: '/api/placeholder/figure4',
      color: '#10b981'
    },
    {
      id: 5,
      title: 'What\'s Really Driving Climate Change in Your Region?',
      type: 'Interactive Data Visualization',
      description: 'Explore the complex atmospheric patterns and feedback loops that determine how climate change affects different parts of the world',
      publication: 'Weizmann Institute of Science - Climate and Planetary Science Research',
      publicationUrl: 'https://www.weizmann.ac.il/EPS/',
      dataNeeded: 'Climate model output data (NetCDF), temperature/precipitation time series, regional maps',
      figureUrl: '/api/placeholder/figure5',
      color: '#6366f1'
    },
    {
      id: 6,
      title: 'How Cells Build Intricate Mineral Sculptures',
      type: 'Slide Deck',
      description: 'Journey inside living cells to witness the remarkable process of biomineralization - nature\'s construction project at the nanoscale',
      publication: 'Advanced Functional Materials, 2024 - Gal Lab (Weizmann) - Macromolecular condensates regulate coccolith formation',
      publicationUrl: 'https://www.weizmann.ac.il/plants/gal/home',
      dataNeeded: 'Cryo-EM images, calcium concentration maps, cell microscopy time-lapse data',
      figureUrl: '/api/placeholder/figure6',
      color: '#0ea5e9'
    }
  ];

  // Word cloud concept weights
  const conceptCloudWords = [
    { text: "dopamine", weight: 40 },
    { text: "tremor", weight: 35 },
    { text: "gait", weight: 30 },
    { text: "balance", weight: 28 },
    { text: "memory", weight: 34 },
    { text: "cognition", weight: 26 },
    { text: "dementia", weight: 38 },
    { text: "parkinson's", weight: 42 },
    { text: "caregiver", weight: 32 },
    { text: "fatigue", weight: 24 },
    { text: "sleep", weight: 22 },
    { text: "constipation", weight: 18 },
    { text: "depression", weight: 20 },
    { text: "anxiety", weight: 21 },
    { text: "progression", weight: 27 },
    { text: "alpha-synuclein", weight: 19 },
    { text: "amyloid", weight: 23 },
    { text: "tau", weight: 21 },
    { text: "inflammation", weight: 25 },
    { text: "neurons", weight: 20 },
    { text: "brain", weight: 29 },
    { text: "diagnosis", weight: 28 },
    { text: "early symptoms", weight: 31 },
    { text: "biomarkers", weight: 26 },
    { text: "imaging", weight: 24 },
    { text: "MRI", weight: 16 },
    { text: "PET", weight: 15 },
    { text: "medication", weight: 27 },
    { text: "levodopa", weight: 22 },
    { text: "DBS", weight: 14 },
    { text: "quality of life", weight: 33 },
    { text: "independence", weight: 30 },
    { text: "caregiving", weight: 29 },
    { text: "research", weight: 26 },
    { text: "clinical trials", weight: 21 },
    { text: "hope", weight: 34 }
  ];

  return {
    overview: {
      totalVisitors: 8380,
      repeatVisitors: 3240,
      avgTimeEngaged: '8:45',
      explainersViewed: 2.4,
      interactionRate: 73,
      conceptsExplored: 8.6,
      topConcept: 'Dopamine replacement therapy',
      topConceptGrowth: 42,
      weeklyData,
      conceptCloudWords
    },
    concepts: concepts.map(c => ({
      ...c,
      volume: c.baseEngagement + Math.floor(Math.random() * 100),
      timeSpent: `${Math.floor(Math.random() * 5) + 3}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
      growth: Math.floor(Math.random() * 50) - 10
    })),
    questionClusters,
    audienceSegments,
    conceptPaths,
    suggestedContent
  };
};

const EngagementDataExplorer = () => {
  const [activeView, setActiveView] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedAudience, setSelectedAudience] = useState('all');
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [expandedSegment, setExpandedSegment] = useState(null);
  const [savedView, setSavedView] = useState(null);
  const [selectedPieSegment, setSelectedPieSegment] = useState('Neuroscience'); // Default to show mockup

  const data = useMemo(() => generateMockData(), []);

  const filteredConcepts = useMemo(() => {
    return data.concepts.filter(concept => 
      concept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      concept.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [data.concepts, searchQuery]);

  const handleExport = (type) => {
    alert(`Exporting ${activeView} data as ${type}. (This is a simulation)`);
  };

  const handleSaveView = (viewName) => {
    setSavedView(viewName);
    alert(`Saved current view as "${viewName}"`);
  };

  return (
    <div className="engagement-explorer">
      {/* Header */}
      <div className="explorer-header">
        <div className="explorer-header-content">
          <div className="explorer-title-section">
            <h1 className="explorer-title">Orbit Engagement Analytics</h1>
            <p className="explorer-subtitle">Understanding your community's curiosity and learning patterns</p>
          </div>
          <div className="explorer-actions">
            <div className="time-range-selector">
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
            </div>
            <div className="export-menu">
              <button className="export-button" onClick={() => handleExport('slides')}>
                <FontAwesomeIcon icon={faDownload} />
                Export
              </button>
              <div className="export-dropdown">
                <button onClick={() => handleExport('PDF Report')}>PDF Report</button>
                <button onClick={() => handleExport('CSV Data')}>CSV Data</button>
                <button onClick={() => handleExport('Slide Deck')}>Slide Deck</button>
                <button onClick={() => handleExport('Images')}>Images</button>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="explorer-tabs">
          <button 
            className={`explorer-tab ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveView('overview')}
          >
            <FontAwesomeIcon icon={faChartLine} />
            Engagement Overview
          </button>
          <button 
            className={`explorer-tab ${activeView === 'concepts' ? 'active' : ''}`}
            onClick={() => setActiveView('concepts')}
          >
            <FontAwesomeIcon icon={faLightbulb} />
            Topic & Concept Explorer
          </button>
          <button 
            className={`explorer-tab ${activeView === 'questions' ? 'active' : ''}`}
            onClick={() => setActiveView('questions')}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
            Question & Curiosity Analysis
          </button>
          <button 
            className={`explorer-tab ${activeView === 'audience' ? 'active' : ''}`}
            onClick={() => setActiveView('audience')}
          >
            <FontAwesomeIcon icon={faUsers} />
            Audience Segmentation
          </button>
          <button 
            className={`explorer-tab ${activeView === 'suggested' ? 'active' : ''}`}
            onClick={() => setActiveView('suggested')}
          >
            <FontAwesomeIcon icon={faMagic} />
            Suggested Content
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="explorer-content">
        {/* Search Bar */}
        {activeView === 'concepts' && (
          <div className="explorer-search">
            <div className="search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input 
                type="text"
                placeholder={activeView === 'concepts' ? "Search concepts, topics, publications..." : "Search question clusters..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            {activeView === 'audience' && (
              <div className="audience-filter">
                <FontAwesomeIcon icon={faFilter} />
                <select value={selectedAudience} onChange={(e) => setSelectedAudience(e.target.value)}>
                  <option value="all">All Audiences</option>
                  <option value="patients">Patients</option>
                  <option value="caregivers">Caregivers</option>
                  <option value="researchers">Researchers</option>
                  <option value="providers">Healthcare Providers</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* View 1: Engagement Overview */}
        {activeView === 'overview' && (
          <div className="overview-view">
            <div className="saved-views">
              <button onClick={() => handleSaveView('Donor Report')}>
                <FontAwesomeIcon icon={faBookmark} /> Save as "Donor Report"
              </button>
              <button onClick={() => handleSaveView('Education Gaps')}>
                <FontAwesomeIcon icon={faBookmark} /> Save as "Education Gaps"
              </button>
              <button onClick={() => handleSaveView('Emerging Interests')}>
                <FontAwesomeIcon icon={faBookmark} /> Save as "Emerging Interests"
              </button>
            </div>

            {/* KPIs */}
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Total Visitors</div>
                  <div className="kpi-value">{data.overview.totalVisitors.toLocaleString()}</div>
                  <div className="kpi-change positive">
                    <FontAwesomeIcon icon={faArrowUp} /> +12% vs last period
                  </div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '67%', background: '#3b82f6' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                  <FontAwesomeIcon icon={faArrowUp} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Repeat Visitors</div>
                  <div className="kpi-value">{data.overview.repeatVisitors.toLocaleString()}</div>
                  <div className="kpi-subtext">{Math.round((data.overview.repeatVisitors / data.overview.totalVisitors) * 100)}% return rate</div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '39%', background: '#10b981' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Avg. Time Engaged</div>
                  <div className="kpi-value">{data.overview.avgTimeEngaged}</div>
                  <div className="kpi-change positive">
                    <FontAwesomeIcon icon={faArrowUp} /> +8% vs last period
                  </div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '58%', background: '#f59e0b' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                  <FontAwesomeIcon icon={faPlay} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Explainers Viewed / Session</div>
                  <div className="kpi-value">{data.overview.explainersViewed}</div>
                  <div className="kpi-change positive">
                    <FontAwesomeIcon icon={faArrowUp} /> +15% vs last period
                  </div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '48%', background: '#8b5cf6' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' }}>
                  <FontAwesomeIcon icon={faEye} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Active Interaction Rate</div>
                  <div className="kpi-value">{data.overview.interactionRate}%</div>
                  <div className="kpi-subtext">Not just scrolling</div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '73%', background: '#ec4899' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="kpi-card highlight">
                <div className="kpi-icon-wrapper" style={{ background: 'white', color: '#4f46e5' }}>
                  <FontAwesomeIcon icon={faLightbulb} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">Concepts Explored / Session</div>
                  <div className="kpi-value">{data.overview.conceptsExplored}</div>
                  <div className="kpi-subtext">Depth of understanding index</div>
                  <div className="kpi-progress-bar">
                    <div className="kpi-progress-fill" style={{ width: '86%', background: 'rgba(255, 255, 255, 0.5)' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Headline Insight */}
            <div className="insight-banner">
              <div className="insight-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div className="insight-content">
                <div className="insight-title">Key Insight This Period</div>
                <div className="insight-text">
                  Interest in <strong>{data.overview.topConcept}</strong> content increased <strong>{data.overview.topConceptGrowth}%</strong> following the new treatment study announcement, 
                  with particularly strong engagement from caregivers and patients in late-stage disease.
                </div>
              </div>
            </div>

            {/* Word Cloud */}
            <div className="wordcloud-section">
              <h3>Most Engaged Concepts</h3>
              <p className="section-description">
                Visual representation of concept engagement frequency. Larger words indicate higher community interest and interaction.
              </p>
              <div className="wordcloud-image-container">
                <img 
                  src="/snorkle_vis_thumbnails/parkinsons_wordcloud.jpg" 
                  alt="Parkinson's and Dementia Engagement Word Cloud"
                  className="wordcloud-static-image"
                />
              </div>
            </div>

            {/* Trend Chart */}
            <div className="chart-section">
              <h3>Engagement Trends</h3>
              <div className="trend-chart">
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color" style={{ background: '#4f46e5' }}></div>
                    <span>Total Visitors</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{ background: '#10b981' }}></div>
                    <span>Repeat Visitors</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{ background: '#f59e0b' }}></div>
                    <span>Interaction Rate %</span>
                  </div>
                </div>
                <div className="chart-bars">
                  {data.overview.weeklyData.map((week, idx) => {
                    const maxVisitors = Math.max(...data.overview.weeklyData.map(w => w.visitors));
                    return (
                      <div key={idx} className="chart-week">
                        <div className="chart-bar-group">
                          <div 
                            className="chart-bar visitors"
                            style={{ height: `${(week.visitors / maxVisitors) * 100}%` }}
                            title={`${week.visitors} visitors`}
                          ></div>
                          <div 
                            className="chart-bar repeat"
                            style={{ height: `${(week.repeatVisitors / maxVisitors) * 100}%` }}
                            title={`${week.repeatVisitors} repeat visitors`}
                          ></div>
                        </div>
                        <div className="chart-week-label">{week.week}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Top Science Questions */}
            <div className="top-questions-section">
              <h3>Top Science Questions This Month</h3>
              <div className="question-list">
                <div className="question-item">
                  <span className="question-rank">1</span>
                  <span className="question-text">How does alpha-synuclein aggregation lead to cell death?</span>
                  <span className="question-count">234 asks</span>
                </div>
                <div className="question-item">
                  <span className="question-rank">2</span>
                  <span className="question-text">What's the connection between gut health and brain symptoms?</span>
                  <span className="question-count">189 asks</span>
                </div>
                <div className="question-item">
                  <span className="question-rank">3</span>
                  <span className="question-text">How do clinical trials determine dosing for new treatments?</span>
                  <span className="question-count">167 asks</span>
                </div>
                <div className="question-item">
                  <span className="question-rank">4</span>
                  <span className="question-text">Why do symptoms fluctuate throughout the day?</span>
                  <span className="question-count">142 asks</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View 2: Topic & Concept Explorer */}
        {activeView === 'concepts' && (
          <div className="concepts-view">
            <div className="concepts-intro">
              <p className="view-description">
                This shows what your community is curious about, not what you told them to read.
              </p>
            </div>

            <div className="concepts-layout">
              <div className="concepts-sidebar">
                <h3>Categories</h3>
                <div className="category-list">
                  {['All', 'Mechanisms', 'Symptoms', 'Treatments', 'Trials', 'Progression'].map(cat => (
                    <button key={cat} className="category-button">
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="concepts-main">
                <div className="concepts-grid">
                  {filteredConcepts.map((concept, idx) => (
                    <div key={idx} className="concept-card">
                      <div className="concept-header">
                        <h4 className="concept-name">{concept.name}</h4>
                        <span className="concept-category">{concept.category}</span>
                      </div>
                      <div className="concept-metrics">
                        <div className="concept-metric">
                          <div className="metric-label">Engagement Volume</div>
                          <div className="metric-value">{concept.volume}</div>
                        </div>
                        <div className="concept-metric">
                          <div className="metric-label">Avg. Time Spent</div>
                          <div className="metric-value">{concept.timeSpent}</div>
                        </div>
                        <div className="concept-metric">
                          <div className="metric-label">Growth</div>
                          <div className={`metric-value ${concept.growth >= 0 ? 'positive' : 'negative'}`}>
                            <FontAwesomeIcon icon={concept.growth >= 0 ? faArrowUp : faArrowDown} />
                            {Math.abs(concept.growth)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="concepts-insights">
                <h3>Related Context</h3>
                <div className="insight-panel">
                  <h4>Representative Questions</h4>
                  <ul>
                    <li>"How does this relate to my daily symptoms?"</li>
                    <li>"Are there clinical trials testing this?"</li>
                    <li>"What does the latest research say?"</li>
                  </ul>
                </div>
                <div className="insight-panel">
                  <h4>Related Publications</h4>
                  <ul>
                    <li>Nature: "Alpha-synuclein pathways" (142 clicks)</li>
                    <li>NEJM: "Novel treatment approaches" (98 clicks)</li>
                    <li>Lancet: "Disease biomarkers" (76 clicks)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Concept Paths */}
            <div className="concept-paths-section">
              <h3>Learning Pathways</h3>
              <p className="section-description">Common exploration patterns showing how users navigate between concepts</p>
              <div className="concept-paths">
                {data.conceptPaths.map((path, idx) => (
                  <div key={idx} className="concept-path">
                    <div className="path-entry">{path.entry}</div>
                    <div className="path-arrow">→</div>
                    <div className="path-followups">
                      {path.followUp.map((follow, fIdx) => (
                        <div key={fIdx} className="path-followup">{follow}</div>
                      ))}
                    </div>
                    <div className="path-frequency">{path.frequency} users</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* View 3: Question & Curiosity Analysis with Suggested Content */}
        {activeView === 'questions' && (
          <div className="questions-view">
            {/* Pie Chart with Publications Panel */}
            <div className="questions-pie-section">
              <div className="pie-section-header">
                <h3>User Interaction by Scientific Category</h3>
                <button className="export-button">
                  <FontAwesomeIcon icon={faDownload} />
                  Export Data
                </button>
              </div>
              
              <div className="pie-with-publications">
                <div className="pie-chart-wrapper">
                  <PieChart 
                    data={data.questionClusters.map(cluster => ({
                      label: cluster.name,
                      value: cluster.questions
                    }))}
                    size={250}
                    selectedSegment={selectedPieSegment}
                  />
                </div>
                
                {/* Publications Panel - Mockup of interaction */}
                {selectedPieSegment && (
                  <>
                    {/* Callout line from selected legend item to panel */}
                    <svg className="callout-line-from-legend" width="120" height="80" viewBox="0 0 120 80" preserveAspectRatio="none">
                      <defs>
                        <marker
                          id="dot-legend"
                          markerWidth="8"
                          markerHeight="8"
                          refX="4"
                          refY="4"
                        >
                          <circle cx="4" cy="4" r="3" fill="#ec4899" />
                        </marker>
                      </defs>
                      {/* Elbow line: horizontal from legend, then diagonal/vertical to panel */}
                      <path
                        d="M 0 40 L 40 40 L 100 15"
                        stroke="#ec4899"
                        strokeWidth="2.5"
                        fill="none"
                        markerStart="url(#dot-legend)"
                      />
                    </svg>
                    
                    <div className="publications-panel">
                    <div className="publications-header">
                      <div className="selected-category">
                        <div className="category-indicator" style={{ background: '#ec4899' }}></div>
                        <div className="category-info">
                          <div className="category-title-row">
                            <h4>{selectedPieSegment}</h4>
                            <span className="interaction-count">
                              {data.questionClusters.find(c => c.name === selectedPieSegment)?.questions.toLocaleString()} interactions
                            </span>
                          </div>
                          <p className="category-subtitle">Publications tagged with this category</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="publications-list">
                      {data.questionClusters
                        .find(c => c.name === selectedPieSegment)
                        ?.publications?.map((pub, idx) => (
                          <a 
                            key={idx}
                            href={pub.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="publication-item"
                          >
                            <img 
                              src={pub.journalLogo} 
                              alt={pub.journal}
                              className="journal-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <div className="pub-content">
                              <div className="pub-title">{pub.title}</div>
                              <div className="pub-authors">{pub.authors}</div>
                              <div className="pub-journal">{pub.journal}</div>
                            </div>
                          </a>
                        ))}
                    </div>
                  </div>
                  </>
                )}
              </div>
            </div>

            {/* Suggested Content Section */}
            <div className="suggested-content-section">
              <h3>Suggested Visualizations from Weizmann Publications</h3>
              <p className="section-description">
                Transform breakthrough research from Weizmann Institute into engaging visual explainers. 
                Each suggestion is based on a real publication and addresses identified learning needs.
              </p>
              
              <div className="suggestions-carousel">
                {data.suggestedContent.map((item) => (
                  <div key={item.id} className="suggestion-module">
                    
                    {/* Content */}
                    <div className="module-content">
                      <div className="module-type-inline">{item.type}</div>
                      <h3 className="module-title">"{item.title}"</h3>
                      
                      <div className="module-description">
                        {item.description}
                      </div>
                      
                      <a 
                        href={item.publicationUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="module-publication-link"
                      >
                        <div className="module-publication">
                          <FontAwesomeIcon icon={faBookmark} />
                          <span>{item.publication}</span>
                        </div>
                      </a>
                      
                      <div className="module-data-needed">
                        <strong>Data:</strong> {item.dataNeeded}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="module-actions">
                      <button 
                        className="module-button primary"
                        onClick={() => alert(`Generating "${item.title}"...\n\nThis would launch the Snorkle visualization creator.`)}
                      >
                        <FontAwesomeIcon icon={faMagic} />
                        Generate & Review
                      </button>
                      <button 
                        className="module-button secondary"
                        onClick={() => alert(`Viewing details for "${item.title}"...`)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* View 4: Audience Segmentation */}
        {activeView === 'audience' && (
          <div className="audience-view">
            <div className="audience-intro">
              <p className="view-description">
                These are learning styles and information needs, not personal profiles. 
                Segmentation based on self-identified roles and engagement preferences.
              </p>
            </div>

            <div className="audience-segments">
              {data.audienceSegments.map((segment, idx) => (
                <div key={idx} className="segment-card">
                  <div 
                    className="segment-header"
                    onClick={() => setExpandedSegment(expandedSegment === idx ? null : idx)}
                  >
                    <div className="segment-title-section">
                      <FontAwesomeIcon 
                        icon={expandedSegment === idx ? faChevronDown : faChevronRight} 
                        className="segment-toggle"
                      />
                      <h3 className="segment-name">{segment.role}</h3>
                    </div>
                    <div className="segment-stats">
                      <div className="segment-stat">
                        <div className="stat-value">{segment.count.toLocaleString()}</div>
                        <div className="stat-label">Users</div>
                      </div>
                      <div className="segment-stat">
                        <FontAwesomeIcon icon={faClock} />
                        <div className="stat-value">{segment.avgTime}</div>
                        <div className="stat-label">Avg. Time</div>
                      </div>
                    </div>
                  </div>

                  {expandedSegment === idx && (
                    <div className="segment-details">
                      <div className="segment-detail-grid">
                        <div className="detail-section">
                          <h4>Top Interests</h4>
                          <ul>
                            {segment.topInterests.map((interest, iIdx) => (
                              <li key={iIdx}>{interest}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="detail-section">
                          <h4>Reading Style</h4>
                          <div className="style-badge">{segment.readingStyle}</div>
                        </div>
                        <div className="detail-section">
                          <h4>Engagement Preference</h4>
                          <div className="style-badge">{segment.engagement}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Segment Comparison */}
            <div className="segment-comparison">
              <h3>Audience Comparison</h3>
              <div className="comparison-table">
                <table>
                  <thead>
                    <tr>
                      <th>Segment</th>
                      <th>Users</th>
                      <th>Avg. Time</th>
                      <th>Concepts/Session</th>
                      <th>Interaction Rate</th>
                      <th>Primary Interest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.audienceSegments.map((segment, idx) => (
                      <tr key={idx}>
                        <td className="segment-name-cell">{segment.role}</td>
                        <td>{segment.count.toLocaleString()}</td>
                        <td>{segment.avgTime}</td>
                        <td>{(Math.random() * 5 + 4).toFixed(1)}</td>
                        <td>{Math.floor(Math.random() * 20 + 65)}%</td>
                        <td className="interest-cell">{segment.topInterests[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Drop-off Analysis */}
            <div className="dropoff-section">
              <h3>Engagement Drop-off by Segment</h3>
              <p className="section-description">Understanding where different audiences disengage helps tailor content</p>
              <div className="dropoff-grid">
                <div className="dropoff-card">
                  <h4>Patients</h4>
                  <div className="dropoff-point">
                    <div className="dropoff-label">Primary drop-off</div>
                    <div className="dropoff-value">Technical mechanism details</div>
                  </div>
                  <div className="dropoff-recommendation">
                    Recommendation: Provide visual analogies and simplified explanations
                  </div>
                </div>
                <div className="dropoff-card">
                  <h4>Caregivers</h4>
                  <div className="dropoff-point">
                    <div className="dropoff-label">Primary drop-off</div>
                    <div className="dropoff-value">Long-form research papers</div>
                  </div>
                  <div className="dropoff-recommendation">
                    Recommendation: Offer executive summaries and key takeaways
                  </div>
                </div>
                <div className="dropoff-card">
                  <h4>Researchers</h4>
                  <div className="dropoff-point">
                    <div className="dropoff-label">Primary drop-off</div>
                    <div className="dropoff-value">Oversimplified content</div>
                  </div>
                  <div className="dropoff-recommendation">
                    Recommendation: Provide links to primary literature and detailed methodology
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View 5: Suggested Content */}
        {activeView === 'suggested' && (
          <div className="suggested-view">
            <div className="suggested-intro">
              <p className="view-description">
                AI-powered content recommendations based on engagement gaps, user questions, and learning pathways. 
                Generate targeted visualizations to address your community's most pressing information needs.
              </p>
            </div>

            {/* Horizontal scrollable carousel-style cards */}
            <div className="suggestions-carousel">
              {data.suggestedContent.map((item) => (
                <div key={item.id} className={`suggestion-module priority-${item.priority.toLowerCase()}`}>
                  {/* Priority ribbon */}
                  <div className={`priority-ribbon priority-${item.priority.toLowerCase()}`}>
                    {item.priority} Priority
                  </div>
                  
                  {/* Visual preview area */}
                  <div className="module-preview" style={{ background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}40 100%)` }}>
                    <div className="module-icon" style={{ background: item.color }}>
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="module-type">{item.type}</div>
                  </div>
                  
                  {/* Content */}
                  <div className="module-content">
                    <h3 className="module-title">{item.title}</h3>
                    
                    <div className="module-description">
                      {item.description}
                    </div>
                    
                    <div className="module-publication">
                      <FontAwesomeIcon icon={faBookmark} />
                      <span>{item.publication}</span>
                    </div>
                    
                    <div className="module-data-needed">
                      <strong>Data Needed:</strong> {item.dataNeeded}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="module-actions">
                    <button 
                      className="module-button primary"
                      onClick={() => alert(`Generating "${item.title}"...\n\nThis would launch the Snorkle visualization creator.`)}
                    >
                      <FontAwesomeIcon icon={faMagic} />
                      Generate & Review
                    </button>
                    <button 
                      className="module-button secondary"
                      onClick={() => alert(`Viewing details for "${item.title}"...`)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary Stats */}
            <div className="suggestions-summary">
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">{data.suggestedContent.filter(s => s.priority === 'High').length}</div>
                  <div className="summary-label">High Priority Items</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faLightbulb} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">+{Math.round(data.suggestedContent.reduce((sum, item) => {
                    const match = item.expectedImpact.match(/\+(\d+)%/);
                    return sum + (match ? parseInt(match[1]) : 0);
                  }, 0) / data.suggestedContent.length)}%</div>
                  <div className="summary-label">Avg. Expected Impact</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="summary-content">
                  <div className="summary-value">5,240</div>
                  <div className="summary-label">Users Affected</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagementDataExplorer;
